/**
 * AlertRecipientsPanel — who receives alert emails (superadmin): rule alerts
 * firing / recovering and the Sol-Ark outage alarm.
 *
 * Two controls, matching Eric's ask: (1) a per-internal-user "receives alerts"
 * toggle, (2) a configurable list of extra addresses (e.g. a shared ops inbox).
 * Recipients = subscribed internal users ∪ extra addresses (server-resolved).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmailListInput } from '@/components/ui/EmailListInput';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-toastify';
import {
  getAlertRecipients,
  saveAlertRecipientEmails,
  setUserAlertSubscribed,
  sendTestAlert,
  sendSampleBatteryAlertEmail,
  type AlertRecipientUser,
} from '@/lib/alertsApi';

/** @param onChanged called after the recipients change (the Alert summary card re-reads its status). */
export function AlertRecipientsPanel({ onChanged }: { onChanged?: () => void } = {}) {
  const [users, setUsers] = useState<AlertRecipientUser[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingEmails, setSavingEmails] = useState(false);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingSample, setSendingSample] = useState(false);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await sendTestAlert();
      const n = r.inAppRecipients;
      const emailMsg = !r.email ? ''
        : r.email.status === 'sent' ? ` · email sent to ${r.email.recipients ?? 0} recipient${r.email.recipients === 1 ? '' : 's'}`
          : r.email.status === 'not_configured' ? ' · email not configured yet'
            : ' · email failed';
      toast.success(`Test alert sent — in-app to ${n} recipient${n === 1 ? '' : 's'}${emailMsg}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send test alert');
    } finally {
      setSendingTest(false);
    }
  };

  // The real battery email with SAMPLE data, to the person who pressed it only.
  const sendSample = async () => {
    setSendingSample(true);
    try {
      const r = await sendSampleBatteryAlertEmail();
      toast.success(`Sample battery alert email sent to ${r.to}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send the sample email');
    } finally {
      setSendingSample(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAlertRecipients();
      setUsers(res.users);
      setEmails([...new Set((res.extra_emails || []).map((e) => e.trim().toLowerCase()).filter(Boolean))]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load alert recipients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const saveEmails = async () => {
    // Accept comma- or newline-separated addresses.
    setSavingEmails(true);
    try {
      const res = await saveAlertRecipientEmails(emails);
      setEmails([...new Set((res.extra_emails || []).map((e) => e.trim().toLowerCase()).filter(Boolean))]);
      toast.success('Alert recipient emails saved');
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save emails');
    } finally {
      setSavingEmails(false);
    }
  };

  const toggleUser = async (u: AlertRecipientUser, next: boolean) => {
    setBusyUser(u.user_id);
    // Optimistic; revert on failure.
    setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, alert_subscribed: next } : x)));
    try {
      await setUserAlertSubscribed(u.user_id, next);
      onChanged?.();
    } catch (err) {
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, alert_subscribed: !next } : x)));
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setBusyUser(null);
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-[#8FB560]" />
        <h3 className="text-base font-semibold text-foreground">Alert recipients</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Alert emails go to the subscribed internal users below, plus any extra addresses.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="mt-4 space-y-6">
          {/* Extra addresses */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Extra email addresses</label>
            <EmailListInput value={emails} onChange={setEmails} placeholder="Add an email address" />
            <div className="mt-2">
              <Button className="bg-[#8FB560] hover:bg-[#7a9d51]" disabled={savingEmails} onClick={() => void saveEmails()}>
                {savingEmails ? 'Saving…' : 'Save addresses'}
              </Button>
            </div>
          </div>

          {/* Internal users */}
          <div>
            <div className="mb-2 text-sm font-medium text-foreground">Internal team</div>
            {users.length === 0 ? (
              <p className="text-sm text-muted-foreground">No internal users.</p>
            ) : (
              <div className="divide-y divide-border rounded-md border border-border">
                {users.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-foreground">{u.name}</div>
                      {u.email && <div className="truncate text-xs text-muted-foreground">{u.email}</div>}
                    </div>
                    <Switch
                      checked={u.alert_subscribed}
                      disabled={busyUser === u.user_id}
                      onCheckedChange={(v) => void toggleUser(u, v)}
                      aria-label={`Toggle alerts for ${u.name}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Test delivery — fire a real alert through the same path to confirm
              the recipients above actually receive it (in-app now; email once
              the Resend env is configured). */}
          <div className="border-t border-border pt-4">
            <div className="mb-1 text-sm font-medium text-foreground">Test delivery</div>
            <p className="mb-2 text-xs text-muted-foreground">
              Send a test alert to confirm everyone above receives it. Uses the same delivery path as real alerts.
            </p>
            <Button variant="outline" disabled={sendingTest} onClick={() => void sendTest()}>
              {sendingTest ? 'Sending…' : 'Send test alert'}
            </Button>
            <p className="mb-2 mt-4 text-xs text-muted-foreground">
              Email yourself a sample battery alert to check its buttons on your phone. It goes only to you, and its links change nothing.
            </p>
            <Button variant="outline" disabled={sendingSample} onClick={() => void sendSample()}>
              {sendingSample ? 'Sending sample…' : 'Send me a sample battery alert email'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
